/**
 * Conta internacional history/movements from IB account statements:
 * GET /v1/ib/accounts/statements → BaaS BalanceTransaction list.
 *
 * Conta Internacional must show the **international ledger only**
 * (FX/EFX, wire/ACH/SWIFT/SEPA, foreign-currency legs) — never the
 * domestic Pix/TED/boleto extract used by Extrato / Pix Área.
 */

import { fetchAccountStatements } from "../Hooks/Transactions";

/** Domestic rails that belong on Extrato / Pix — never Conta Internacional. */
const DOMESTIC_LEDGER_TYPES = new Set([
  "PIX",
  "TED",
  "BOLETO",
  "BILL",
  "CONTAS",
  "INTERNAL_TRANSFER",
  "VALUT_WITHDRAWAL_REQUEST",
]);

/** International rails + FX (EFX product) stamped on BalanceTransaction.Type. */
const INTERNATIONAL_LEDGER_TYPES = new Set([
  "FX",
  "EFX",
  "WIRE",
  "ACH",
  "SWIFT",
  "SEPA",
]);

const FOREIGN_CURRENCY_CODES = [
  "USD",
  "EUR",
  "PYG",
  "CAD",
  "CHF",
  "JPY",
  "AUD",
  "MXN",
  "SEK",
  "GBP",
];

const toMajorUnits = (cents) => Number(cents || 0) / 100;

const formatStatementDate = (createdAt) => {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return String(createdAt);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", "");
};

const statementType = (item) => String(item?.type || "").trim().toUpperCase();

const statementCurrency = (item) =>
  String(item?.currency_code || item?.currencyCode || "")
    .trim()
    .toUpperCase();

const statementHaystack = (item) =>
  `${item?.description || ""} ${item?.type || ""} ${item?.category?.name || ""}`.toUpperCase();

/**
 * Keep only international-account ledger rows.
 * Excludes Pix-only (and other domestic) extract noise; keeps FX/EFX + foreign rails.
 */
export const isInternationalLedgerItem = (item) => {
  if (!item) return false;

  const type = statementType(item);
  if (DOMESTIC_LEDGER_TYPES.has(type)) return false;

  // Pix described without a typed international rail (category/description bleed).
  const haystack = statementHaystack(item);
  if (/\bPIX\b/.test(haystack) && !INTERNATIONAL_LEDGER_TYPES.has(type)) return false;

  if (INTERNATIONAL_LEDGER_TYPES.has(type)) return true;

  const currency = statementCurrency(item);
  if (currency && currency !== "BRL") return true;

  // FX/conversion copy or international rail names even when Type/currency_code is missing.
  if (/(?:\bFX\b|\bEFX\b|CONVERS|SWAP|C[AÂ]MBIO|EXCHANGE|\bWIRE\b|\bSWIFT\b|\bSEPA\b|\bACH\b)/.test(haystack)) {
    return true;
  }

  return FOREIGN_CURRENCY_CODES.some((code) => new RegExp(`\\b${code}\\b`).test(haystack));
};

const inferAsset = (item) => {
  const explicit = statementCurrency(item);
  if (explicit) return explicit.toLowerCase();

  const haystack = statementHaystack(item);
  const found = [...FOREIGN_CURRENCY_CODES, "BRL"].find((code) =>
    new RegExp(`\\b${code}\\b`).test(haystack),
  );
  return (found || "USD").toLowerCase();
};

/** Parse `USD→EUR` / `USD -> EUR` pairs from FX descriptions. */
const parseFxPair = (item) => {
  const haystack = `${item?.description || ""}`;
  const match = haystack.match(/\b([A-Za-z]{3})\s*(?:→|->|⇒)\s*([A-Za-z]{3})\b/);
  if (!match) return null;
  return {
    from: match[1].toLowerCase(),
    to: match[2].toLowerCase(),
  };
};

const inferHistoryKind = (item) => {
  const amount = Number(item?.amount ?? 0);
  const type = statementType(item).toLowerCase();
  const operation = String(item?.operation || "").toLowerCase();
  const haystack = `${item?.description || ""} ${item?.type || ""} ${item?.category?.name || ""}`.toLowerCase();

  if (
    type === "fx"
    || type === "efx"
    || /convers|swap|câmbio|cambio|\bfx\b|\befx\b|exchange/.test(haystack)
  ) {
    return "conversions";
  }

  if (
    ["wire", "ach", "swift", "sepa"].includes(type)
    || /wire|swift|sepa|\bach\b|transfer|envio/.test(haystack)
  ) {
    if (operation === "credit" || amount > 0) return "purchases";
    return "transfers";
  }

  if (operation === "credit" || amount > 0) return "purchases";
  if (/withdraw|saque|cash.?out/.test(haystack)) return "withdrawals";
  return "withdrawals";
};

const inferMovementKind = (item) => {
  const historyKind = inferHistoryKind(item);
  if (historyKind === "conversions") return "swap";
  if (historyKind === "purchases") return "deposit";
  if (historyKind === "transfers") return "transfer";
  return "internal";
};

const statusFromItem = () => "Concluída";

const statementOperationId = (item) =>
  String(item?.operation_id || item?.operationId || "").trim();

/** Map a BaaS statement row → Conta internacional general history item. */
export const mapStatementToHistoryItem = (item) => {
  const amountCents = Number(item?.amount ?? 0);
  const absMajor = Math.abs(toMajorUnits(amountCents));
  const asset = inferAsset(item);
  const historyKind = inferHistoryKind(item);
  const date = formatStatementDate(item?.created_at || item?.createdAt);
  const method = item?.type || item?.category?.name || item?.description || "";
  const id = String(item?.id || item?.operation_id || `${date}-${amountCents}`);
  const fxPair = parseFxPair(item);

  const base = {
    id,
    date,
    asset,
    amount: absMajor,
    status: statusFromItem(item),
    method,
    description: item?.description || "",
    raw: item,
    historyKind,
  };

  switch (historyKind) {
    case "purchases":
      return {
        ...base,
        amountBrl: absMajor,
        rate: null,
      };
    case "withdrawals":
      return {
        ...base,
        destination: item?.description || "—",
      };
    case "transfers":
      return {
        ...base,
        recipient: item?.description || "—",
      };
    case "conversions":
      return {
        ...base,
        from: fxPair?.from || asset,
        to: fxPair?.to || "brl",
        amountFrom: absMajor,
        amountTo: absMajor,
        rate: null,
      };
    default:
      return base;
  }
};

/**
 * Collapse FX/EFX debit+credit legs that share operation_id into one conversion row.
 */
export const mapFxLegsToConversionHistoryItem = (legs = []) => {
  if (!Array.isArray(legs) || legs.length === 0) return null;

  const sorted = [...legs].sort(
    (a, b) => Number(a?.amount ?? 0) - Number(b?.amount ?? 0),
  );
  const debit = sorted.find((leg) => Number(leg?.amount ?? 0) < 0) || sorted[0];
  const credit =
    sorted.find((leg) => Number(leg?.amount ?? 0) > 0)
    || sorted[sorted.length - 1];

  const pair =
    parseFxPair(debit) || parseFxPair(credit) || parseFxPair(legs[0]) || {};
  const fromAsset = pair.from || inferAsset(debit);
  const toAsset = pair.to || inferAsset(credit);
  const amountFrom = Math.abs(toMajorUnits(debit?.amount ?? 0));
  const amountTo = Math.abs(toMajorUnits(credit?.amount ?? 0));
  const date = formatStatementDate(
    debit?.created_at || debit?.createdAt || credit?.created_at || credit?.createdAt,
  );
  const opId = statementOperationId(debit) || statementOperationId(credit);

  return {
    id: opId || String(debit?.id || credit?.id || `${date}-fx`),
    date,
    asset: fromAsset,
    amount: amountFrom,
    status: statusFromItem(debit),
    method: debit?.type || credit?.type || "FX",
    description: debit?.description || credit?.description || "",
    raw: { legs },
    historyKind: "conversions",
    from: fromAsset,
    to: toAsset,
    amountFrom,
    amountTo,
    rate: amountFrom > 0 ? amountTo / amountFrom : null,
  };
};

/** Map a BaaS statement row → Conta internacional per-currency movement. */
export const mapStatementToMovement = (item) => {
  const amountCents = Number(item?.amount ?? 0);
  const major = toMajorUnits(amountCents);
  const fxPair = parseFxPair(item);
  const currency = inferAsset(item);
  return {
    id: String(item?.id || item?.operation_id || `${item?.created_at}-${amountCents}`),
    date: formatStatementDate(item?.created_at || item?.createdAt),
    currency,
    kind: inferMovementKind(item),
    amount: major,
    description: item?.description || item?.type || "Movimentação",
    status: statusFromItem(item),
    counterCurrency: fxPair
      ? (fxPair.from === currency ? fxPair.to : fxPair.from)
      : undefined,
    raw: item,
  };
};

const sortByDateDesc = (a, b) => {
  const da = a.date || "";
  const db = b.date || "";
  return db.localeCompare(da);
};

/**
 * Build Conta Internacional general history from statement rows:
 * filter to international ledger, pair FX legs, map the rest.
 */
export const buildContaInternacionalHistory = (items = []) => {
  const international = items.filter(isInternationalLedgerItem);
  const fxGroups = new Map();
  const other = [];

  international.forEach((item) => {
    const type = statementType(item);
    const opId = statementOperationId(item);
    if ((type === "FX" || type === "EFX") && opId) {
      if (!fxGroups.has(opId)) fxGroups.set(opId, []);
      fxGroups.get(opId).push(item);
      return;
    }
    other.push(item);
  });

  const conversions = [...fxGroups.values()]
    .map(mapFxLegsToConversionHistoryItem)
    .filter(Boolean);

  const mappedOther = other.map(mapStatementToHistoryItem);
  return [...mappedOther, ...conversions].sort(sortByDateDesc);
};

export const buildContaInternacionalMovements = (items = [], { currencyId } = {}) => {
  const movements = items
    .filter(isInternationalLedgerItem)
    .map(mapStatementToMovement);
  if (!currencyId) return movements;
  const id = String(currencyId).toLowerCase();
  return movements.filter((row) => row.currency === id);
};

const loadStatementItems = async () => {
  const response = await fetchAccountStatements({
    fetch_all: true,
    max_items: 500,
  });
  const payload = response?.data ?? response ?? {};
  return Array.isArray(payload.items) ? payload.items : [];
};

export const fetchContaInternacionalHistory = async () => {
  const items = await loadStatementItems();
  return buildContaInternacionalHistory(items);
};

export const fetchContaInternacionalMovements = async ({ currencyId } = {}) => {
  const items = await loadStatementItems();
  return buildContaInternacionalMovements(items, { currencyId });
};
